import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

const today = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

const TermsOfService = () => {
    return (
        <div className="theme-horizon" data-testid="terms-page">
            <SEO
                title="Terms of Service · SouthTrip.in"
                description="Terms and conditions for using SouthTrip.in cab booking, airport transfers and tour packages in Kerala. Booking, cancellation and payment terms."
                path="/terms"
            />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
                <header className="mb-10">
                    <div className="hz-pill">Legal</div>
                    <h1 className="font-hz-display text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
                        Terms of Service
                    </h1>
                    <p className="text-[var(--ink-soft)] mt-3 text-sm">Last updated: {today}</p>
                </header>

                <section className="space-y-6 text-[var(--ink-soft)] leading-relaxed text-[15px] md:text-base">
                    <p>
                        These Terms of Service (&quot;Terms&quot;) govern your use of the website southtrip.in and any
                        booking made through it. The website is operated by <strong>LuxSaCabs</strong> (referred to as
                        &quot;SouthTrip&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), based at 35 P/N,
                        Kavarapparambu Road, Angamaly, Kerala 683572, India. By using southtrip.in or making a
                        booking, you agree to these Terms.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">1. Service description</h2>
                    <p>SouthTrip provides the following services across Kerala and South India:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Cab booking</strong> — point-to-point or full-day private cabs with a driver</li>
                        <li><strong>Airport transfers</strong> — from/to Cochin (COK), Trivandrum (TRV) and Calicut (CCJ) airports</li>
                        <li><strong>Tour packages</strong> — multi-day curated Kerala itineraries with cab, driver and (optionally) accommodation</li>
                        <li><strong>Concierge add-ons</strong> — hotel reservations, restaurant booking, Ayurveda treatments, ticketing (on request)</li>
                    </ul>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">2. Booking process</h2>
                    <ol className="list-decimal pl-6 space-y-1">
                        <li>You submit a booking request via the website, WhatsApp or phone.</li>
                        <li>SouthTrip confirms availability within 4 hours and sends a written confirmation by email and/or WhatsApp.</li>
                        <li>Your booking is considered confirmed only after you receive a written confirmation from SouthTrip.</li>
                        <li>A booking reference (e.g. <code>bkg-xxxxxxxx</code>) is generated for each confirmed trip.</li>
                    </ol>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">3. Payment terms</h2>
                    <p>
                        SouthTrip uses a <strong>pay-direct-to-driver</strong> model. No advance or pre-payment is
                        required for cab bookings and airport transfers. Payment is made directly to the driver at
                        the end of the trip, by:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Cash (INR only)</li>
                        <li>UPI (PhonePe, Google Pay, Paytm — driver QR code)</li>
                        <li>Credit / debit card (where available — POS machine on request)</li>
                    </ul>
                    <p>
                        A GST invoice is sent by email after the trip. For tour packages an advance deposit may be
                        requested at confirmation; the exact terms are stated in your package quote.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">4. Cancellation policy</h2>
                    <p>
                        <strong>Cab bookings &amp; airport transfers:</strong> free cancellation up to 24 hours before
                        the pickup time. Within 24 hours, a cancellation charge of <strong>50 % of the trip fare</strong> may
                        apply.
                    </p>
                    <p>
                        <strong>Tour packages:</strong> please refer to our{" "}
                        <Link to="/refund-policy" className="text-[var(--brand)] underline">Refund Policy</Link>{" "}
                        for the full cancellation and refund schedule.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">5. Your responsibilities</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Provide accurate pickup details, contact number and flight number (for airport pickups).</li>
                        <li>Be ready at the pickup point at the confirmed time. Waiting beyond 30 min may incur waiting charges (₹150/hour).</li>
                        <li>Pay applicable tolls, parking and inter-state permit fees at actuals (these are excluded from the base fare unless explicitly stated).</li>
                        <li>Treat the driver and vehicle with respect. Smoking and consumption of alcohol inside the vehicle are not permitted.</li>
                    </ul>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">6. Liability limitations</h2>
                    <p>
                        SouthTrip and its driver-partners carry valid commercial vehicle insurance and a public-service
                        permit issued by the Kerala Motor Vehicles Department. Our liability is limited to the value of
                        the booking. SouthTrip is not liable for:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Delays caused by force majeure (weather, strikes, road blocks, political unrest)</li>
                        <li>Loss or damage to personal belongings left in the vehicle</li>
                        <li>Indirect, consequential or incidental damages</li>
                    </ul>
                    <p>We strongly recommend that travellers carry valid travel insurance.</p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">7. Intellectual property</h2>
                    <p>
                        All content on southtrip.in (logos, text, images, code) is the property of LuxSaCabs and is
                        protected by Indian and international copyright law. Reproduction without written permission is
                        prohibited.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">8. Governing law &amp; jurisdiction</h2>
                    <p>
                        These Terms are governed by the laws of India, in particular the Indian Contract Act 1872 and
                        the Consumer Protection Act 2019. Any dispute shall be subject to the exclusive jurisdiction of
                        the courts of <strong>Ernakulam, Kerala, India</strong>.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">9. Contact</h2>
                    <p>
                        <strong>SouthTrip.in by LuxSaCabs</strong><br />
                        35 P/N, Kavarapparambu Road, Angamaly, Kerala 683572, India<br />
                        Email:{" "}
                        <a href="mailto:info@southtrip.in" className="text-[var(--brand)] underline">info@southtrip.in</a><br />
                        Phone: <a href="tel:+918075814740" className="text-[var(--brand)] underline">+91 80-75814740</a>
                    </p>

                    <p className="text-xs text-[var(--ink-softer)] pt-8 border-t border-[var(--border)] mt-12">
                        See also: <Link to="/privacy-policy" className="hz-link">Privacy Policy</Link>{" · "}
                        <Link to="/refund-policy" className="hz-link">Refund Policy</Link>
                    </p>
                </section>
            </article>
        </div>
    );
};

export default TermsOfService;
